<script setup>
import { ref, markRaw, computed, onMounted, watch, nextTick, provide } from 'vue';
import { useRoute, useRouter } from "vue-router";
import api from '@/lib/api.js';
import { downloadResultPDF } from '@/utils/pdfDownloader.js'; 

import PrincipalPage1 from '@/views/result/MLSRD-2026/principal/Page1.vue';
import PrincipalPage2 from '@/views/result/MLSRD-2026/principal/Page2.vue';
import PrincipalPage3 from '@/views/result/MLSRD-2026/principal/Page3.vue';
import PrincipalPage4 from '@/views/result/MLSRD-2026/principal/Page4.vue';
import PrincipalPage5 from '@/views/result/MLSRD-2026/principal/Page5.vue';
import PrincipalPage6 from '@/views/result/MLSRD-2026/principal/Page6.vue';
import PrincipalPage11 from '@/views/result/MLSRD-2026/principal/Page11.vue';
import PrincipalPage12 from '@/views/result/MLSRD-2026/principal/Page12.vue';
import PageHeader from '@/components/result/MLSRD-2026/PageHeader.vue';
import PageFooter from '@/components/result/MLSRD-2026/PageFooter.vue';

import ResultSwiper from '@/components/result/MLSRD-2026/Swiper.vue'; 
import { SwiperSlide } from 'swiper/vue'; 
import { useLoadingStore } from '@/stores/loading'; 

const route = useRoute();
const router = useRouter(); 
const loadingStore = useLoadingStore();

// 학교 결과 데이터
const schoolData = ref(null);
const loading = ref(true);

const props = defineProps({
  schoolType: {
    type: String,
    default: 'elementary' // 기본값은 초등학교용 
  }
});

const currentSwiperKey = ref(0); // ResultSwiper를 강제로 재마운트하기 위한 key

// 동적 페이지 구성 데이터 생성 함수
const generatePageData = (data) => {
  if (!data || !data.totalList || !data.classList) {
    return [
      { component: PrincipalPage1 },
      { component: PrincipalPage2 },
      { component: PrincipalPage3 },
      { component: PrincipalPage11 },
      { component: PrincipalPage12 }
    ];
  }

  const pages = [];
  const { totalList, classList } = data;
  
  // 기본 페이지들 (1-3)
  pages.push(
    { component: PrincipalPage1 },
    { component: PrincipalPage2 },
    { component: PrincipalPage3 }
  );
  
  // 학년별로 처리
  totalList.forEach(gradeData => {
    const gradeCode = gradeData.grade_code;
    const gradeClasses = classList.filter(c => c.grade_code === gradeCode);
    
    // 학년별 문해력/수리력 개요 페이지 (Page4, Page5 재사용)
    pages.push(
      { 
        component: PrincipalPage4, // 문해력
        props: { 
          gradeCode, 
          subject: 'literacy'
        }
      },
      { 
        component: PrincipalPage5, // 수리력
        props: { 
          gradeCode, 
          subject: 'numeracy'
        }
      }
    );
    
    // 반별 페이지들 (3개반씩 그룹핑, Page6 재사용)
    for (let i = 0; i < gradeClasses.length; i += 3) {
      const classGroup = gradeClasses.slice(i, i + 3);
      pages.push({
        component: PrincipalPage6, // 기존 Page6 재사용
        props: {
          gradeCode,
          classesData: classGroup
        }
      });
    }
  });
  
  // 마지막 페이지들 (11-12)
  pages.push(
    { component: PrincipalPage11 },
    { component: PrincipalPage12 }
  );
  
  return pages;
};

const activeSchoolType = computed(() => {
    // API 데이터가 있으면 schul_type을 기반으로 결정
    console.log("schoolData:", schoolData.value);
    console.log("schul_type:", schoolData.value?.schul_type);
    
    if (schoolData.value && schoolData.value.schul_type) {
        const apiSchoolType = schoolData.value.schul_type;
        console.log("apiSchoolType:", apiSchoolType);
        
        if (apiSchoolType === 'E') {
            console.log("초등학교로 설정");
            return 'elementary';
        }
        if (apiSchoolType === 'M' || apiSchoolType === 'H') {
            console.log("중고등학교로 설정");
            return 'secondary'; // 중등과 고등은 동일
        }
    }
    
    console.log("기본값 사용:", props.schoolType || 'elementary');
    // 기본값 또는 props 사용
    return props.schoolType || 'elementary';
});

// 하위 컴포넌트에 전달할 schoolType (컴포넌트에서 기대하는 형태로 변환)
const componentSchoolType = computed(() => {
    if (schoolData.value && schoolData.value.schul_type) {
        const apiSchoolType = schoolData.value.schul_type;
        console.log("componentSchoolType - apiSchoolType:", apiSchoolType);
        
        if (apiSchoolType === 'E') {
            console.log("컴포넌트에 elementary 전달");
            return 'elementary';
        }
        if (apiSchoolType === 'M') {
            console.log("컴포넌트에 middle 전달");
            return 'middle';
        }
        if (apiSchoolType === 'H') {
            console.log("컴포넌트에 high 전달");
            return 'high';
        }
    }
    
    // 기본값
    return 'elementary';
});

const currentPrincipalPages = computed(() => {
  console.log("schoolData for page generation:", schoolData.value);
  
  const pageData = generatePageData(schoolData.value);
  console.log("생성된 페이지 데이터:", pageData.length);
  
  return pageData;
});

const totalSlides = computed(() => currentPrincipalPages.value.length); 

const subHeaderText = computed(() => {
  const schoolName = schoolData.value?.classList?.[0]?.schul_nm || '학교';
  return `${schoolName} 결과`;
});

// 헤더용 서브헤더 텍스트 (학교장용)
const headerSubText = computed(() => {
  const schoolName = schoolData.value?.classList?.[0]?.schul_nm || '학교';
  return `${schoolName} 결과`;
});

const studentGroup = computed(() => {
    if (activeSchoolType.value === 'elementary') return '초등학생';
    if (activeSchoolType.value === 'middle') return '중학생';
    if (activeSchoolType.value === 'high') return '고등학생';
    return '학생';
});

// Pinia 스토어 메시지를 업데이트하는 공통 함수
const updateLoadingStoreMessages = () => {
  let mainMsg = '학교장용 검사 결과 보고서 다운로드';
  let subMsg = '학교 단위로 학생들의 문해력, 수리력의 척도점수와 영역별 도달도 등 평균적인 분석 결과와 대시보드를 제공합니다.'; 
  mainMsg += ` (${totalSlides.value}페이지)`;

  loadingStore.setLoadingMessages(
    mainMsg, 
    subMsg,
    '학교장용' 
  );
};

// API 호출 함수
const fetchSchoolResult = async () => {
  try {
    loading.value = true;
    const { exam_id, schul_code } = route.query;
    
    if (!exam_id || !schul_code) {
      throw new Error('필수 파라미터가 없습니다.');
    }

    console.log('School 결과 API 요청:', {
      exam_id,
      schul_code
    });

    const response = await api.post('/exam/tea/resultSchoolDetail', {
      exam_id,
      schul_code
    });

    if (response.data.success) {
      schoolData.value = response.data.result;
      console.log('School 결과 데이터:', schoolData.value);
      
      // PDF 파일명 생성을 위해 전역에 저장
      window.currentSchoolData = schoolData.value;
    } else {
      throw new Error(response.data.message || '데이터를 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('School 결과 데이터 조회 실패:', error);
  } finally {
    loading.value = false;
  }
};

const viewSecondaryReport = () => {   
    router.push({ name: 'ResultPrincipal2026', params: { schoolType: 'middle' } });    
};

// ResultSwiper를 재마운트시키는 함수
const forceSwiperRemount = () => {
    currentSwiperKey.value++; // key 값을 변경하여 ResultSwiper를 새로 만듬
};

// PDF 다운로드 함수
const downloadPDF = async () => {
  try {
    console.log('교장용 PDF 다운로드 시작');
    
    loadingStore.startDownloading();
    
    // 파일명 생성
    let filename = null;
    if (schoolData.value && schoolData.value.totalList && schoolData.value.totalList.length > 0) {
      try {
        const schoolName = schoolData.value.totalList[0].schul_nm || '학교';
        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
        filename = `${schoolName}_학교장용_검사결과_${dateStr}.pdf`;
      } catch (e) {
        console.warn('학교 데이터 사용 실패:', e);
      }
    }
    
    // 기본 파일명 설정
    if (!filename) {
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
      filename = `학교장용_검사결과_${dateStr}.pdf`;
    }
    
    // PDF 생성 진행률 업데이트
    const onProgress = (progress) => {
      console.log('PDF 생성 진행률:', progress);
    };
    
    // PDF 생성 및 다운로드
    const result = await downloadResultPDF({
      filename: filename,
      onProgress: onProgress,
      containerSelector: '.result-container',
      pageSelector: '.page-wrapper'
    });
    
    console.log('교장용 PDF 다운로드 완료:', result);
    loadingStore.stopDownloading();
    
  } catch (error) {
    console.error('교장용 PDF 다운로드 실패:', error);
    loadingStore.stopDownloading();
    alert('PDF 생성 중 오류가 발생했습니다. 다시 시도해주세요.');
  }
};

// 하위 컴포넌트에서 사용할 수 있도록 provide
provide('schoolData', schoolData);
provide('loading', loading);
provide('downloadPDF', downloadPDF);

onMounted(() => { 
  console.log(`Mounted. Initial props.schoolType: ${props.schoolType}`);
  fetchSchoolResult(); // API 호출 추가
  nextTick(() => {
    updateLoadingStoreMessages();
  });
});

watch(() => props.schoolType, async (newSchoolType) => {        
    await nextTick();    
    updateLoadingStoreMessages(); 
    forceSwiperRemount(); 
}, { immediate: true }); 
</script>

<template>
  <!-- <button 
    v-if="activeSchoolType === 'elementary'"
    @click="viewSecondaryReport" 
    class="view-secondary-btn"
  >
    중학생 결과 보고서 보기
  </button> -->

  <div v-if="!loading" class="result-container" style="background-color: white;">
    <!-- 학교 데이터를 PDF 생성기에서 접근할 수 있도록 숨겨진 요소에 저장 -->
    <div v-if="schoolData" style="display: none;" data-school-data :data-school-data="JSON.stringify(schoolData)" :data-download-handler="downloadPDF"></div>
    
    <div v-for="(pageData, index) in currentPrincipalPages" :key="index" class="page-wrapper">
      <PageHeader 
        :page-index="index" 
        report-type="principal" 
        :sub-header-text="headerSubText" 
      />
      <component 
        :is="pageData.component" 
        :schoolType="componentSchoolType" 
        v-bind="pageData.props || {}" 
        v-if="pageData.component" 
      />
      <PageFooter 
        :current-page="index + 1" 
        :total-pages="totalSlides" 
      />
    </div>
  </div>
  
  <div v-else class="loading-container">
    <p>데이터를 불러오는 중...</p>
  </div> 


</template>

<style scoped>
.result-container {
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 20px 0;
}

.page-wrapper {
  width: 100%;
  min-height: 100vh;
  border-bottom: 1px solid #e5e5e5;
  display: flex;
  flex-direction: column;
}

.page-wrapper:last-child {
  border-bottom: none;
}

/* 페이지 콘텐츠 영역 */
.page-wrapper > :nth-child(2) {
  flex: 1;
  padding: 20px;
}

/* PDF 출력 시 */
@media print {
  .result-container {
    gap: 0;
    padding: 0;
  }
  
  .page-wrapper {
    min-height: auto;
    padding: 0;
    border-bottom: none;
    page-break-after: always;
    page-break-inside: avoid;
  }
  
  .page-wrapper:last-child {
    page-break-after: avoid;
  }
  
  .page-wrapper > :nth-child(2) {
    padding: 10px;
  }
}
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 18px;
  color: #666;
}

.view-secondary-btn {
  position: absolute;
  top: 20px;
  right: 20px; 
  background-color: #f0ad4e; 
  padding: 10px 15px;
  border: 0;
  border-radius: 5px;
  cursor: pointer;
  z-index: 500; 
  font-size: 16px;
}
</style>